import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './index.css';
import GlobalStyles from './components/GlobalStyles';

// Import pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import ConstructionPage from './pages/construction/ConstructionPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import SupportPage from './pages/support/SupportPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ProfilePage from './pages/profile/ProfilePage';
import PaymentsPage from './pages/payments/PaymentsPage';
import MaterialCatalogPage from './pages/MaterialCatalogPage';
import MyVastuPage from './pages/dashboard/MyVastuPage';
import MyConstructionPage from './pages/dashboard/MyConstructionPage';

// Import admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ProjectManagement from './pages/admin/ProjectManagement';
import ProjectDetails from './pages/admin/ProjectDetails';
import DocumentManagement from './pages/admin/DocumentManagement';
import SitePhotos from './pages/admin/SitePhotos';
// import MaterialManagement from './pages/admin/MaterialManagement';
import SupportManagement from './pages/admin/SupportManagement';
import PaymentManagement from './pages/admin/PaymentManagement';
import CustomerManagement from './pages/admin/CustomerManagement';
import Analytics from './pages/admin/Analytics';

// Import invite page
import AcceptInvitePage from './pages/invite/AcceptInvitePage';
import ResetPasswordPage from './pages/ResetPasswordPage';

function App() {
  // Check for existing authentication on app load
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('token');
    return !!token;
  });

  const [userRole, setUserRole] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role || 'customer';
  });

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUserRole('customer');
  };

  // Check if user is admin
  const isAdmin = userRole === 'admin';

  return (
    <Router>
      <GlobalStyles />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/invite/accept" element={<AcceptInvitePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={
            isAuthenticated ? 
              (isAdmin ? <Navigate to="/admin" replace /> : <Navigate to="/dashboard" replace />) : 
              <LoginPage setIsAuthenticated={setIsAuthenticated} setUserRole={setUserRole} />
          } />
          
          {/* Admin routes */}
          {isAdmin && (
            <Route path="/admin" element={
              <AdminLayout setIsAuthenticated={setIsAuthenticated} onLogout={handleLogout} />
            }>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="projects" element={<ProjectManagement />} />
              <Route path="projects/:id" element={<ProjectDetails />} />
              <Route path="documents" element={<DocumentManagement />} />
              <Route path="site-photos" element={<SitePhotos />} />
              {/* <Route path="materials" element={<MaterialManagement />} /> */}
              <Route path="support" element={<SupportManagement />} />
              <Route path="payments" element={<PaymentManagement />} />
              <Route path="customers" element={<CustomerManagement />} />
              <Route path="analytics" element={<Analytics />} />
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
            </Route>
          )}
          
          {/* Customer routes with dashboard layout */}
          <Route path="/" element={
            isAuthenticated ? 
              (isAdmin ? <Navigate to="/admin" replace /> : <DashboardLayout setIsAuthenticated={setIsAuthenticated} onLogout={handleLogout} />) : 
              <Navigate to="/login" replace />
          }>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="construction" element={<MyConstructionPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="materials" element={<MaterialCatalogPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="vastu" element={<MyVastuPage />} />
            <Route index element={<Navigate to="/dashboard" replace />} />
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={
            isAuthenticated ? 
              (isAdmin ? <Navigate to="/admin" replace /> : <Navigate to="/dashboard" replace />) : 
              <Navigate to="/login" replace />
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
